import { useEffect } from 'react';
import { useLocation } from 'react-router';
import { headLinksFor, parsePath } from '@/config/routes';
import { metaFor } from '@/copy/meta-en';
import { structuredDataJson } from '@/copy/structuredData';
import { DEFAULT_LOCALE, isLocale, type Locale } from '@/strings/types';

/* Marks the tags this hook owns so it can replace its own output on every
 * client-side navigation. The marker's job is to separate hook-owned tags from
 * the OG and Twitter tags that src/prerender/head.ts wrote into the page at
 * build time: this hook CLEARS everything marked before re-emitting, so
 * marking a social tag would delete it from the live DOM on hydration. See the
 * marker policy in src/prerender/head.ts. */
const OWNED = 'data-pn-head';

function appendMeta(name: string, content: string) {
  const el = document.createElement('meta');
  el.setAttribute('name', name);
  el.setAttribute('content', content);
  el.setAttribute(OWNED, '');
  document.head.appendChild(el);
}

/* A <script> whose type is not a JavaScript MIME type is a data block: the HTML
 * spec never executes it, so public/_headers' script-src 'self' does not apply
 * and no nonce is needed. textContent (not innerHTML) means the JSON is stored
 * verbatim rather than re-parsed as markup. */
function appendJsonLd(json: string) {
  const el = document.createElement('script');
  el.type = 'application/ld+json';
  el.setAttribute(OWNED, '');
  el.textContent = json;
  document.head.appendChild(el);
}

/* Owns the whole per-route document head: <title>, meta description,
 * robots noindex on not-found paths, <link rel="canonical">, the hreflang
 * alternates, and the JSON-LD graph on the routes that carry one.
 *
 * Since phase 5 this hook is no longer how those tags first reach the page.
 * Every route is prerendered, and src/prerender/head.ts writes the same tags
 * into each page's own HTML file at build time, computed from the same sources
 * this hook reads (config/routes.ts for URLs, copy/meta-*.ts for strings). A
 * crawler that runs no JavaScript now sees all of them.
 *
 * What this hook still owns is CLIENT-SIDE NAVIGATION. React Router changes
 * the URL without a document load, so the prerendered head belongs to the page
 * the visitor arrived on, not the one they navigated to. This replaces it.
 * The two must agree: if you change what is emitted here, change head.ts with
 * it, or a soft navigation will contradict the served HTML. */
export function useDocumentHead(): void {
  const { pathname } = useLocation();

  useEffect(() => {
    const head = document.head;
    /* Eager clear (existing discipline). The bare attribute selector covers
     * every element kind this hook owns — the JSON-LD <script> as well as
     * link + meta — which is why it is not narrowed to a tag name. */
    for (const stale of Array.from(head.querySelectorAll(`[${OWNED}]`))) {
      stale.remove();
    }

    const match = parsePath(pathname);
    if (!match) {
      /* Not-found: localized title (same locale coercion as Root), an explicit
       * noindex, and no canonical/description — a 404 must not claim a URL. */
      const [first = ''] = pathname.split('/').filter(Boolean);
      const displayLocale: Locale = isLocale(first) ? first : DEFAULT_LOCALE;
      document.title = metaFor(displayLocale).notFound.title;
      appendMeta('robots', 'noindex');
      return;
    }

    const page = metaFor(match.locale).pages[match.stem];
    document.title = page.title;
    appendMeta('description', page.description);

    for (const link of headLinksFor(match.locale, match.stem)) {
      const el = document.createElement('link');
      el.setAttribute(OWNED, '');
      el.rel = link.rel;
      if ('hreflang' in link && link.hreflang) el.hreflang = link.hreflang;
      el.href = link.href;
      head.appendChild(el);
    }

    const ld = structuredDataJson(match.locale, match.stem);
    if (ld) appendJsonLd(ld);
  }, [pathname]);
}
